import { RootState } from "../index";
import {
  createDraftSafeSelector,
  createEntityAdapter,
  createSlice,
  EntityId
} from '@reduxjs/toolkit';
import {
  createImage,
  destroyImage,
  fetchImage,
  fetchImages,
  updateImage,
  groupDestroyImage
} from "../../actions/community/imagesActions";

export interface ImageInterface {
  url: string;

  id: number;
  target_id: number;
  target_type: 'Post';

  file_url: string;

  created_at: string;
  updated_at: string;
}

type SilentType = { [key: string]: { ids: number[], count: number } };

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  silent: SilentType;

  editId: EntityId | null;
  editError: string | null;

  newParams: ImageInterface | undefined;
  newError: string | null;

  newGroupParams: ImageInterface | undefined;
  newGroupError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  silent: {},

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null,

  newGroupParams: undefined,
  newGroupError: null,
};

const imagesAdapter = createEntityAdapter<ImageInterface>();

const slice = createSlice({
  name: 'images',
  initialState: imagesAdapter.getInitialState(initialState),
  reducers: {
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setNewGroupParams: (state, action) => {
      state.newGroupParams = action.payload;
      state.newGroupError  = initialState.newGroupError;
    },
    setNewGroupError: (state, action) => {
      state.newGroupError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchImages.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchImages.fulfilled, (state, action) => {
        if (action.payload.silentId) {
          state.silent[action.payload.silentId] = state.silent[action.payload.silentId] || { ids: [], count: 0 };

          state.silent[action.payload.silentId].ids = action.payload.ids;
          state.silent[action.payload.silentId].count = action.payload.count;
          state.entities = { ...state.entities, ...action.payload.images };
        } else {
          state.ids = action.payload.ids;
          state.entities = action.payload.images;
          state.count = action.payload.count;
        }

        state.status = 'succeeded';
      })
      .addCase(fetchImages.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchImage.fulfilled, (state, action) => {
        const image = Object.values(action.payload.image)[0];
        state.entities[image.id] = image;
      })

      // #create
      .addCase(createImage.fulfilled, (state, action) => {
        const image = Object.values(action.payload.image)[0];
        state.entities[image.id] = image;
        state.ids.unshift(image.id);
      })
      .addCase(createImage.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateImage.fulfilled, (state, action) => {
        const image = Object.values(action.payload.image)[0];
        state.entities[image.id] = image;
      })
      .addCase(updateImage.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #destroy
      .addCase(destroyImage.fulfilled, (state, action) => {
        imagesAdapter.removeOne(state, action.payload.id);
      })
      .addCase(groupDestroyImage.fulfilled, (state, action) => {
        imagesAdapter.removeMany(state, action.payload.ids);
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setNewParams,
  setNewError,
  setNewGroupParams,
  setNewGroupError,
  setEditId,
  setEditError
} = slice.actions;

export const selectSilent = (state: RootState) => state.community.images.silent;
export const selectSilentIds = createDraftSafeSelector(
  [selectSilent, (state, silent) => silent],
  (silent, silentId) => silent ? silent[silentId]?.ids : []
);
export const selectSilentCount = createDraftSafeSelector(
  [selectSilent, (_state, silent) => silent],
  (silent, silentId) => silent ? silent[silentId]?.count : []
);

export const {
  selectById: selectImageById,
  selectIds: selectImageIds,
  selectEntities: selectImageEntities,
  selectAll: selectAllImages,
} = imagesAdapter.getSelectors<RootState>(state => state.community.images);
